/**
 * API Service pentru Appointments (Programări)
 */

import apiClient from './api';
import {
  AppointmentListItem,
  AppointmentRead,
  AppointmentCreate,
  AppointmentUpdate,
  AppointmentsListResponse,
  AppointmentsFilters,
  AvailabilityResponse,
} from '../types/appointments';

// ============================================================
// CRUD
// ============================================================

export const getAppointments = async (
  filters: AppointmentsFilters = {}
): Promise<AppointmentsListResponse> => {
  const params = new URLSearchParams();
  
  if (filters.device_id) params.append('device_id', filters.device_id);
  if (filters.patient_id) params.append('patient_id', filters.patient_id);
  if (filters.date_from) params.append('date_from', filters.date_from);
  if (filters.date_to) params.append('date_to', filters.date_to);
  if (filters.status) params.append('status', filters.status);
  if (filters.priority) params.append('priority', filters.priority);
  if (filters.search) params.append('search', filters.search);
  if (filters.skip !== undefined) params.append('skip', String(filters.skip));
  if (filters.limit !== undefined) params.append('limit', String(filters.limit));
  
  const response = await apiClient.get(`/appointments?${params.toString()}`);
  return response.data;
};

export const getAppointment = async (id: string): Promise<AppointmentRead> => {
  const response = await apiClient.get(`/appointments/${id}`);
  return response.data;
};

export const createAppointment = async (data: AppointmentCreate): Promise<AppointmentRead> => {
  const response = await apiClient.post('/appointments', data);
  return response.data;
};

export const updateAppointment = async (
  id: string, 
  data: AppointmentUpdate
): Promise<AppointmentRead> => {
  const response = await apiClient.patch(`/appointments/${id}`, data);
  return response.data;
};

// ============================================================
// Status Transitions
// ============================================================

export const cancelAppointment = async (
  id: string, 
  reason: string
): Promise<AppointmentRead> => {
  const response = await apiClient.post(`/appointments/${id}/cancel`, { reason });
  return response.data;
};

export const checkinAppointment = async (
  id: string, 
  notes?: string
): Promise<AppointmentRead> => {
  const response = await apiClient.post(`/appointments/${id}/checkin`, { notes });
  return response.data;
};

export const startAppointment = async (id: string): Promise<AppointmentRead> => {
  const response = await apiClient.post(`/appointments/${id}/start`);
  return response.data;
};

export const completeAppointment = async (
  id: string, 
  data?: { study_id?: string; notes?: string }
): Promise<AppointmentRead> => {
  const response = await apiClient.post(`/appointments/${id}/complete`, data || {});
  return response.data;
};

export const noshowAppointment = async (id: string): Promise<AppointmentRead> => {
  const response = await apiClient.post(`/appointments/${id}/noshow`);
  return response.data;
};

// ============================================================
// Availability
// ============================================================

export const getDeviceAvailability = async (
  deviceId: string,
  targetDate: string,
  durationMinutes: number = 30
): Promise<AvailabilityResponse> => {
  const params = new URLSearchParams();
  params.append('target_date', targetDate);
  params.append('duration_minutes', String(durationMinutes));
  
  const response = await apiClient.get(
    `/appointments/availability/${deviceId}?${params.toString()}`
  );
  return response.data;
};

export default {
  getAppointments,
  getAppointment,
  createAppointment,
  updateAppointment,
  cancelAppointment,
  checkinAppointment,
  startAppointment,
  completeAppointment,
  noshowAppointment,
  getDeviceAvailability,
};
